import React, { useEffect, useRef } from 'react';
import './licht.css';

// Der Hintergrund der Startseite (Kopf der Header-Version 11) für alle Unterseiten: warmer Verlauf,
// Key-Light, gezeichnete Lichtstrahlen von rechts oben, Staub, der im Lichtkegel treibt, weiche
// Lichtkreise, Vignette und Korn. Liegt absolut oben auf der Seite, deckt den ganzen ersten
// Bildschirm ab und läuft unten weich in den Seitengrund aus — nichts wird mittendrin abgeschnitten.
// Zeichnet nur, solange er zu sehen ist; ohne Bewegung (reduced motion) einmal ein Standbild.

// vorne: nur die Ebene ÜBER dem Inhalt (Staub, Lichtkreise, Vignette, Korn) — wie auf der Startseite,
// wo der Staub über das Panel treibt. ohneStaub: Hintergrund ohne Staub (wenn eine Vorne-Ebene ihn zeichnet).
export function starteLicht(sz, { vorne = false, ohneStaub = false } = {}) {
  const html = s => { const d = document.createElement('div'); d.innerHTML = s.trim(); return d.firstElementChild; };
  sz.innerHTML = vorne
    ? `<canvas class="lsz-staub"></canvas><div class="lsz-vignette"></div><div class="lsz-korn"></div>`
    : `<div class="lsz-bg"></div><div class="lsz-key"></div><div class="lsz-boden"></div>
    <canvas class="lsz-strahlen"></canvas>${ohneStaub ? '' : '<canvas class="lsz-staub"></canvas>'}
    <div class="lsz-vignette"></div><div class="lsz-korn"></div>`;
  const strahlen = sz.querySelector('.lsz-strahlen'), staubC = sz.querySelector('.lsz-staub');
  const bokeh = vorne ? [[88, 12, 140], [70, 80, 90], [96, 58, 190]] : [[78, 18, 120], [64, 62, 80], [90, 74, 150], [52, 22, 60]];
  bokeh.forEach(([x, y, d]) => sz.insertBefore(html(`<div class="lsz-bokeh" style="left:${x}%; top:${y}%; width:${d}px; height:${d}px"></div>`), sz.querySelector('.lsz-vignette')));

  let staub = [];
  function zeichneStrahlen() {
    const w = sz.clientWidth, h = sz.clientHeight;
    if (!w || !h) return;
    const qx = w * 1.04, qy = -h * .28, ziel = Math.atan2(h * .95 - qy, w * .32 - qx);
    if (staubC) zeichneStaubAnlage(w, h, qx, qy, ziel);
    if (!strahlen) return;
    strahlen.width = w; strahlen.height = h;
    const c = strahlen.getContext('2d');
    c.clearRect(0, 0, w, h); c.globalCompositeOperation = 'lighter';
    for (let i = 0; i < 8; i++) {
      const a = ziel + (i - 3.5) * .055 + Math.sin(i * 7.1) * .012, breite = .018 + (i * 37 % 10) / 400, laenge = Math.hypot(w, h) * (.9 + (i * 53 % 10) / 30);
      const g = c.createLinearGradient(qx, qy, qx + Math.cos(a) * laenge, qy + Math.sin(a) * laenge);
      const st = .55 + (i * 29 % 10) / 25;
      g.addColorStop(0, `rgba(255,150,70,${st})`); g.addColorStop(.45, `rgba(255,130,50,${st * .55})`); g.addColorStop(.75, 'rgba(255,120,40,0)');
      c.fillStyle = g; c.beginPath(); c.moveTo(qx, qy);
      c.lineTo(qx + Math.cos(a - breite) * laenge, qy + Math.sin(a - breite) * laenge);
      c.lineTo(qx + Math.cos(a + breite) * laenge, qy + Math.sin(a + breite) * laenge); c.closePath(); c.fill();
    }
  }
  function zeichneStaubAnlage(w, h, qx, qy, ziel) {
    staubC.width = w; staubC.height = h;
    staub = Array.from({ length: 170 }, (_, i) => {
      const d = Math.pow(((i * 0.618) % 1), 1.6) * .85 + .08, a = ziel + (((i * 0.37) % 1) - .5) * .42;
      const gross = i % 23 === 0;
      return { qx, qy, d, a, r: gross ? 2.6 + (i % 3) : .5 + (i % 5) * .3, al: gross ? .25 : .35 + (i % 7) / 14, v: .004 + (i % 9) / 3000, ph: i };
    });
    staub.L = Math.hypot(w, h);
  }
  function zeichneStaub(t) {
    if (!staubC) return;
    const c = staubC.getContext('2d'); c.clearRect(0, 0, staubC.width, staubC.height);
    for (const p of staub) {
      const d = (p.d + t * p.v) % .95, x = p.qx + Math.cos(p.a) * d * staub.L + Math.sin(t * .6 + p.ph) * 6, y = p.qy + Math.sin(p.a) * d * staub.L + Math.cos(t * .5 + p.ph) * 4;
      const fade = Math.min(1, d * 6) * (1 - Math.max(0, (d - .6) / .35));
      c.fillStyle = `rgba(255,200,150,${(p.al * fade).toFixed(3)})`;
      c.beginPath(); c.arc(x, y, p.r, 0, Math.PI * 2); c.fill();
    }
  }

  let aus = false, rafId = 0, sichtbar = true, zeit = 0, vorher = performance.now();
  zeichneStrahlen();
  zeichneStaub(0);
  const ro = new ResizeObserver(() => { if (!aus) { zeichneStrahlen(); zeichneStaub(zeit); } });
  ro.observe(sz);
  // Handy wie reduzierte Bewegung: Staub einmal zeichnen statt 60-mal pro Sekunde (ruckelte beim Scrollen)
  if (matchMedia('(prefers-reduced-motion: reduce), (max-width: 860px)').matches) return () => { aus = true; ro.disconnect(); };

  function lauf(now) {
    rafId = 0;
    if (aus || !sichtbar) return;
    zeit += Math.min(.1, Math.max(0, (now - vorher) / 1000)); vorher = Math.max(vorher, now);
    zeichneStaub(zeit);
    rafId = requestAnimationFrame(lauf);
  }
  const io = new IntersectionObserver(([e]) => {
    sichtbar = e.isIntersecting;
    if (sichtbar && !rafId) { vorher = performance.now(); rafId = requestAnimationFrame(lauf); }
  });
  io.observe(sz);
  return () => { aus = true; cancelAnimationFrame(rafId); io.disconnect(); ro.disconnect(); };
}

export default function Lichtszene({ vorne = false, ohneStaub = false }) {
  const ref = useRef(null);
  useEffect(() => starteLicht(ref.current, { vorne, ohneStaub }), [vorne, ohneStaub]);
  return <div className={'lsz' + (vorne ? ' lsz-vorn' : '')} ref={ref} aria-hidden="true" />;
}
